import React, { useState, useEffect, useMemo } from "react";
import {
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import _ from "lodash";
import InventoryLeftSection from "../components/Inventory/CreateInventory/InventoryLeftSection";
import InventoryRightSection from "../components/Inventory/CreateInventory/InventoryRightSection";
import {
  DESC_FOR_PROPERTY_DETAILS_LOADING,
  TITLE_FOR_PROPERTY_DETAILS_LOADING,
  inventoryPageContents,
  yrMaxInValid,
  YEAR_BUILT_RANGE,
  LOCATION_REQUIRED,
} from "../constants/createInventory";
import {
  saveMemberInventoryData,
  getInventoryById,
} from "../services/inventoryService";
import { pageURLs } from "@/constants";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DEFAULT_MAP_ZOOM,
  INVENTORY_KEY,
  IS_LAND,
  LONG_RENT,
  SELL,
  SHORT_RENT,
  USER_INFO_KEY,
} from "../constants/common";
import {
  checkTutorialDisplayStatus,
  clearLocalStorage,
  createPreSignedUrl,
  createStaticMapImage,
  getLocalStorage,
  getUserId,
  loggedInUser,
  removeLocalStorage,
  setLocalStorage,
  yearValidation,
} from "../helpers/common";
import { userLogout } from "../reducers/login";
import { useDispatch } from "react-redux";
import { uploadMapImageToS3 } from "../helpers/S3/uploadToS3";
import { fetchUserDetails } from "../actions/userprofile";
import {
  getUserDetails,
  getUserStatistics,
  getUserPrivileges,
} from "../services/userprofileService";
import BasicPlanModal from "../components/PlanUpgrade/BasicPlanModal";

const USER_DETAILS_KEY = "userDetails";
const USER_STATISTICS_KEY = "userStatistics";
const USER_PRIVILEGES_KEY = "userPrivileges";

const ConsumerCreateInventory = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [activeStep, setActiveStep] = useState(1);
  const [draftSelectedItem, setDraftSelectedItem] = useState(null);
  let adAmenities = null;
  let isDisabled = true;
  const sessionData = loggedInUser();
  const [showBasicPlanModal, setBasicPlanModal] = useState(false);
  const BasicPlanModalClose = () => setBasicPlanModal(false);
  const BasicPlanModalShow = () => setBasicPlanModal(true);

  const queryClient = useQueryClient();
  const sessionUserId = _.get(sessionData, "userInfo.userId", "");

  const wantAdId = location.state && location.state["wantAdId"];

  const userDetailsQuery = useQuery({
    queryKey: [USER_DETAILS_KEY],
    queryFn: () => getUserDetails(),
    enabled: !!sessionUserId,
  });

  const userDetails = useMemo(
    () => _.get(userDetailsQuery.data, "result[0]", null) || null,
    [userDetailsQuery.data]
  );

  // Mirror the fetched user details into Redux so the shared BasicPlanModal,
  // which reads state.userProfile.userDetails to derive the active plan, keeps
  // working without issuing a second network request.
  useEffect(() => {
    if (userDetailsQuery.data) {
      dispatch(fetchUserDetails.fulfilled(userDetailsQuery.data, "rq-mirror"));
    }
  }, [userDetailsQuery.data, dispatch]);

  const userStatisticsQuery = useQuery({
    queryKey: [USER_STATISTICS_KEY, userDetails?.User_Plan_Id],
    queryFn: () => getUserStatistics({ UserPlanId: userDetails?.User_Plan_Id }),
    enabled: !!userDetails,
  });

  const userPrivilegesQuery = useQuery({
    queryKey: [USER_PRIVILEGES_KEY, userDetails?.PlanId],
    queryFn: () => getUserPrivileges({ PlanId: userDetails?.PlanId }),
    enabled: !!userDetails?.PlanId,
  });

  const userCount = useMemo(
    () => _.get(userStatisticsQuery.data, "result[0]", {}) || {},
    [userStatisticsQuery.data]
  );
  const allowedCount = useMemo(
    () => _.get(userPrivilegesQuery.data, "result[0]", {}) || {},
    [userPrivilegesQuery.data]
  );

  const updateReloadParam = () => {
    queryClient.invalidateQueries({ queryKey: [USER_DETAILS_KEY] });
    queryClient.invalidateQueries({ queryKey: [USER_STATISTICS_KEY] });
    queryClient.invalidateQueries({ queryKey: [USER_PRIVILEGES_KEY] });
  };

  useEffect(() => {
    if (userCount?.Inventory >= allowedCount?.Inventory) {
      BasicPlanModalShow();
    }
  }, [userCount, allowedCount]);

  useEffect(() => {
    //Need to show inventory onboard very first time
    let DisplayInventoryAddOnboard = checkTutorialDisplayStatus(7); //Inventory onboard display
    if (DisplayInventoryAddOnboard) {
      navigate(pageURLs.gitchaInvetoryOnBoard, {
        state: { isFromIntroduction: true, wantAdId: wantAdId },
      });
    }
  }, []);

  useEffect(() => {
    adAmenities = getLocalStorage(INVENTORY_KEY);
    const userInfo = _.get(getLocalStorage(USER_INFO_KEY), "userInfo", null);
    if (!userInfo) {
      handleStateClear();
    }
    const currentActiveStep = _.get(adAmenities, "actvStep", "");
    const cmpldPerctg = _.get(adAmenities, "cmpldPerctg", "");
    if (currentActiveStep) {
      setActiveStep(currentActiveStep);
      setCurrentCompletedPercentage(cmpldPerctg);
      setFormValues(adAmenities);
    }
  }, []);

  // to set is edit false
  useEffect(() => {
    let statesToBeUpdated = { actvStep: activeStep };
    if (activeStep == 5) {
      statesToBeUpdated = {
        isEdit: false,
        actvStep: activeStep,
      };
    }
    handleFormValues(statesToBeUpdated);
  }, [activeStep]);

  const [continueButtonState, setContinueButtonState] = useState(isDisabled);
  const [formValues, setFormValues] = useState(adAmenities);
  const [showConfirmStatuModal, setConfirmStatus] = useState(false);
  const propertyPurpose = _.get(formValues, "proprtyPurpose", []);
  const pageContents = _.get(inventoryPageContents, `step_${activeStep}`, "");

  const {
    title,
    description,
    skipButtonEnable,
    exitButtonText,
    backButtonEnable,
    completedPercentage,
    continueButtonText,
    hideContinueButton,
  } = pageContents;
  const [currentCompletedPercentage, setCurrentCompletedPercentage] =
    useState(completedPercentage);

  let newTitle = title;
  let newDescription = description;
  if (activeStep === 3 && _.get(formValues, "isPropertyExist", false)) {
    // For show title/desc if property details loading in map.
    newTitle = TITLE_FOR_PROPERTY_DETAILS_LOADING;
    newDescription = DESC_FOR_PROPERTY_DETAILS_LOADING;
  } else {
    if (propertyPurpose) {
      if (propertyPurpose.length === 1) {
        if (_.includes(propertyPurpose, SELL)) {
          newTitle = newTitle.replace(
            "[TITLE]",
            "How much would you sell your property for?"
          );
          newDescription = newDescription.replace(
            "[DESC]",
            "Set an asking price for your property so we can match you to want-listings. This can be adjusted when you send an introduction. "
          );
        }

        if (_.includes(propertyPurpose, LONG_RENT)) {
          newTitle = newTitle.replace(
            "[TITLE]",
            "How much would you rent your property for each month?"
          );
          newDescription = newDescription.replace(
            "[DESC]",
            "You can adjust your asking price when you send each introduction. Our goal is to match your property to want-listings. "
          );
        }

        if (_.includes(propertyPurpose, SHORT_RENT)) {
          newTitle = newTitle.replace(
            "[TITLE]",
            "How much would you rent your property for per night on average?"
          );
          newDescription = newDescription.replace(
            "[DESC]",
            "You can consider requested dates and adjust the price accordingly before introducing this property to potential renters."
          );
        }
      } else if (propertyPurpose.length > 1) {
        newTitle = newTitle.replace(
          "[TITLE]",
          "How much are you willing to price your property at?"
        );
        newDescription = newDescription.replace(
          "[DESC]",
          "You can adjust your asking price when you send each introduction. Our goal is to match your property to want-listings"
        );
      }
    }
  }

  const handleStateClear = () => {
    dispatch(userLogout());
    clearLocalStorage();
    navigate(pageURLs.home);
  };

  useEffect(() => {
    setFormValues((prevState) => ({
      ...prevState,
      actvStep: activeStep,
      cmpldPerctg: currentCompletedPercentage,
    }));
  }, [currentCompletedPercentage]);
  useEffect(() => {
    setLocalStorage(INVENTORY_KEY, formValues);
  }, [formValues]);

  const handleConfirmationModal = (modalState) => {
    setConfirmStatus(modalState);
  };
  const mapImageMutation = useMutation({
    mutationFn: async () => {
      const lat = _.get(formValues, "lat", "");
      const lng = _.get(formValues, "lng", "");
      const mapZoom = _.get(formValues, "mapZoom", DEFAULT_MAP_ZOOM);
      const mapResponse = await createStaticMapImage(lat, lng, mapZoom);
      if (!mapResponse) {
        alert("Something went wrong while fetching map image.");
        return null;
      }
      const preSignedUrlRes = await createPreSignedUrl();
      if (preSignedUrlRes && _.get(preSignedUrlRes, "errorCode") === 0) {
        const preSignedUrlsArray = _.get(preSignedUrlRes, "result", []);
        const preSignedS3Url = preSignedUrlsArray
          ? _.get(preSignedUrlsArray[0], "presignedUrl", "")
          : "";
        const s3FullUrl = await uploadMapImageToS3(preSignedS3Url, mapResponse);
        return s3FullUrl || null;
      }
      return null;
    },
    onSuccess: (s3FullUrl) => {
      if (s3FullUrl) {
        const imageUrl = s3FullUrl.split("?")[0];
        handleFormValues({ mapImg: imageUrl });
      }
    },
  });

  const saveInventoryMutation = useMutation({
    mutationFn: saveMemberInventoryData,
  });

  /**
   * To handle map image upload
   */
  const handleMapImageUpload = () => {
    mapImageMutation.mutate();
  };


  /**
   * To handle validation
   * @param {*} currentStep
   */
  const handleContinueClick = (currentStep) => {
    // to remove edit filed from session
    handleFormValues({ isEdit: false });
    const isConfirmAll = _.get(formValues, "isConfirmAll", false);
    let errorSection = null;
    if (currentStep === 1) {
      handleDraftItemClick(draftSelectedItem);
    }
    if (currentStep === 3) {
      const yearBuilt = _.get(formValues, "yearBuilt", false);
      const hasAddress =
        !!_.get(formValues, "locAddress", "") &&
        !!_.get(formValues, "lat", "") &&
        !!_.get(formValues, "lng", "");
      const stepErrors = [];
      if (!hasAddress) {
        stepErrors.push(LOCATION_REQUIRED);
      }
      if (yearBuilt && !yearValidation(yearBuilt.toString())) {
        stepErrors.push(YEAR_BUILT_RANGE);
      }
      if (stepErrors.length > 0) {
        errorSection = yrMaxInValid;
        // validationErrors is what the step-3 form actually renders;
        // invFormError alone is never read by any component.
        handleFormValues({
          validationErrors: stepErrors,
          updatedAt: Date.now(),
        });
      } else {
        handleFormValues({ validationErrors: [] });
        handleMapImageUpload();
      }
    }
    if (currentStep === 7) {
      const isAddInventry = true;
      saveMemberInventory(isAddInventry);
    } else {
      if (errorSection) {
        handleFormValues({
          invFormError: errorSection,
          updatedAt: Date.now(),
        }); // 'updatedAt' used for rerendering the component if the form values are not changing.
      } else {
        const errorSection = _.get(
          getLocalStorage(INVENTORY_KEY),
          "invFormError"
        );
        if (errorSection) {
          handleFormValues({ invFormError: "", updatedAt: "" }); // Clear errosSection from state.
        }
        if (currentCompletedPercentage <= completedPercentage) {
          setCurrentCompletedPercentage(completedPercentage);
        }
        currentStep !== 1 && setActiveStep(activeStep + 1);
      }
    }
  };

  const handleDraftItemClick = async (draftSelectedItem) => {
    const currentStep = _.get(draftSelectedItem, "currentStep", null);
    const selectedId = _.get(draftSelectedItem, "selectedId", null);
    if (selectedId) {
      await getInventoryById(selectedId)
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode === 0 && responseResult) {
            setFormValues(responseResult);
            if (currentStep === 1) setActiveStep(2);
            else setActiveStep(currentStep);
            setCurrentCompletedPercentage(_.get(responseResult, "cmpldPerctg"));
          } else {
            console.log("Something went wrong");
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  };
  /**
   * To save inventory data
   * @param {*} isAddInventry
   */
  const saveMemberInventory = (isAddInventry) => {
    handleFormValues({ isEdit: false });

    const userId = getUserId(); //_.get(user, "userInfo.userId", null);

    let requestPayLoad = formValues;
    if (!requestPayLoad) {
      requestPayLoad = getLocalStorage(INVENTORY_KEY);
    }
    // const proprtyPurpose = _.get(requestPayLoad, "proprtyPurpose", []);
    // const is_Sell_Selected = _.includes(proprtyPurpose, SELL);
    // if (!is_Sell_Selected) {
    //   requestPayLoad.sellAskngPrice = "";
    // }
    requestPayLoad.cmpldStep = _.get(formValues, "actvStep", 1);
    if (userId && requestPayLoad) {
      if (isAddInventry) {
        requestPayLoad.IsSavedDraft = false;
        requestPayLoad.IsPublished = true;
        delete requestPayLoad.tlText;
        delete requestPayLoad.prptyTypeTxt;
        delete requestPayLoad.step6Errors;
      } else {
        requestPayLoad.IsSavedDraft = true;
        requestPayLoad.IsPublished = false;
      }

      saveInventoryMutation
        .mutateAsync(requestPayLoad)
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode === 0 && responseResult) {
            if (isAddInventry) {
              if (!requestPayLoad.inventoryId) {
                setLocalStorage("MatchSelectedClient", {
                  FirstName: requestPayLoad.clientName,
                  LastName: requestPayLoad.clientName,
                  AgentAsSeller: requestPayLoad.isAgentCreated,
                  label: requestPayLoad.clientName,
                  value: requestPayLoad.clientId,
                });
                setLocalStorage("selectedPropertyId", {
                  Inv_Id: _.get(responseResult, "inventoryId", ""),
                  Location: requestPayLoad.locAddress,
                  City: requestPayLoad.city,
                  Is_LongTerm_Renter: _.includes(propertyPurpose, LONG_RENT),
                  Is_Seller: _.includes(propertyPurpose, SELL),
                  Is_ShortTerm_Renter: _.includes(propertyPurpose, SHORT_RENT),
                  State: requestPayLoad.state,
                  photo: requestPayLoad.photos && requestPayLoad.photos[0],
                });
              }


            } else {
              removeLocalStorage(INVENTORY_KEY);
            }
            if (isAddInventry) {
              const savedInventoryId = _.get(responseResult, "inventoryId", "");
              navigate(`${pageURLs.inventoryDetails}/${savedInventoryId}`, {
                state: { isFromConfirmationPage: true, showSuccessModal: true },
              });
            } else {
              navigate(pageURLs.consumerInventoryListing, {
                state: { showDraft: true },
              });
            }
          } else {
            console.log("Something went wrong");
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    } else {
      handleStateClear();
    }
  };

  const handleBackButtonClick = () => {
    setActiveStep(activeStep - 1);
  };
  /**
   * To handle skip button
   * @param {*} step
   */
  const handleSkipButtonClick = (step) => {
    switch (step) {
      case 4:
        setFormValues((prevState) => ({
          // Clear data from states for nice to have
          ...prevState,
          nice2Have: [],
        }));
        break;
    }
    if (currentCompletedPercentage <= completedPercentage) {
      setCurrentCompletedPercentage(completedPercentage);
    }
    setActiveStep(activeStep + 1);
  };

  const clearPreviousStates = (params) => {
    if (params) {
      const { type } = params;

      if (type === "clearSell") {
        const array = { ...formValues }; // make a separate copy of the state
        [
          "sellAskngPrice",
          "isCompnsteAgent",
          "salesPercnt",
          "nice2Have",
        ].forEach((e) => delete array[e]);
        setFormValues(array);
      }
      if (type === "clearPropertyPurposeLTR") {
        const array = { ...formValues }; // make a separate copy of the state
        [
          "alwLrgDogs",
          "alwSmlDogs",
          "alwCats",
          "LTRAskngPrce",
          "isCompnsteAgent_LTR",
          "salesPercnt_LTR",
          "nice2Have",
        ].forEach((e) => delete array[e]);
        setFormValues(array);
      } else if (type === "clearPropertyPurposeSTR") {
        const array = { ...formValues }; // make a separate copy of the state
        [
          "alwPets",
          "accsPvtBthRm",
          "pvtEntrnce",
          "noOfBeds",
          "STRAskngPrce",
          "isCompnsteAgent_STR",
          "salesPercnt_STR",
          "nice2Have",
        ].forEach((e) => delete array[e]);
        setFormValues(array);
      }
    } else {
      // clear previous state values if user changes property type (eg from lot-land to other types and vice versa)
      const array = { ...formValues }; // make a separate copy of the state
      [
        "locAddress",
        "noBedRm",
        "noBathRm",
        "sqFt",
        "stories",
        "lotSize",
        "grgeSpots",
        "adaAccess",
        "acreage",
        "lotType",
        "ltTypeTxt",
        "roadType",
        "rdTypeTxt",
        "isPowerAccess",
        "waterSuply",
        "wSText",
        "wasteWtrSys",
        "WstWatrSys",
        "watrWtrText",
        "isApprovedStrctr",
        "isInFloodPlain",
        "proprtyPurpose",
        "alwLrgDogs",
        "alwSmlDogs",
        "alwCats",
        "alwPets",
        "accsPvtBthRm",
        "isConfirmOwner",
        "isConfirmPropLoc",
        "nice2Have",
        "sellAskngPrice",
        "LTRAskngPrce",
        "STRAskngPrce",
        "isCompnsteAgent",
        "salesPercnt",
        "isCompnsteAgent_LTR",
        "isCompnsteAgent_STR",
        "salesPercnt_LTR",
        "salesPercnt_STR",
        "photos",
        "invtryDetails",
        "prptySubTypes",
        "prptySubType",
        "prptySubTypeTxt",
        "prptyTypeTxt",
        "IsNewConstruction",
        "isPrvtDrInst",
        "isDrldWelInst",
        "isDrldWelAppr",
        "isSepTankInst",
        "isSepTankAppr",
        "pvtEntrnce",
        "neighborHood",
        "highSchool",
        "yearBuilt",
        "noOfBeds",
        "lat",
        "lng",
        "mapZoom",
        "mapType",
        "street",
        "city",
        "county",
        "state",
        "zip",
      ].forEach((e) => delete array[e]);

      setFormValues(array);
      setCurrentCompletedPercentage(completedPercentage);
    }
  };

  const handleContinueButtonState = (buttonState) => {
    isDisabled = false;
    setContinueButtonState(buttonState ? buttonState : isDisabled);
  };

  // Nothing in the step-3 subtree reports button state, so resuming a draft
  // straight into it left Continue permanently disabled. Enable it and let
  // handleContinueClick surface the validation errors instead.
  useEffect(() => {
    if (activeStep === 3) {
      setContinueButtonState(false);
    }
  }, [activeStep]);

  const handleFormValues = (stateObject) => {
    Object.keys(stateObject).forEach(function (key) {
      setFormValues((prevState) => ({
        ...prevState,
        [key]: stateObject[key],
      }));
    });
  };

  const handleSaveAndExit = (step) => {
    if (step <= 3) {
      removeLocalStorage(INVENTORY_KEY);
      navigate(pageURLs.home);
    } else {
      const isAddInventory = false;
      saveMemberInventory(isAddInventory);
    }
  };


  return (
    <div className="container-fluid position-relative half-fluid h-100 ps-0 pe-0">
      <div className="container-fluid container-height ps-0 pe-0">
        <div className="row h-100 g-0">
          <InventoryLeftSection
            title={newTitle}
            description={newDescription}
            completedPercentage={currentCompletedPercentage}
          />
          <InventoryRightSection
            setActiveStep={setActiveStep}
            setDraftSelectedItem={setDraftSelectedItem}
            draftSelectedItem={draftSelectedItem}
            handleFormValues={handleFormValues}
            handleSaveAndExit={handleSaveAndExit}
            handleContinueClick={handleContinueClick}
            handleBackButtonClick={handleBackButtonClick}
            handleContinueButtonState={handleContinueButtonState}
            isDisabled={continueButtonState}
            skipButtonEnable={skipButtonEnable}
            exitButtonText={exitButtonText}
            backButtonEnable={backButtonEnable}
            continueButtonText={continueButtonText}
            hideContinueButton={hideContinueButton}
            formValues={formValues}
            step={activeStep}
            handleSkipButtonClick={handleSkipButtonClick}
            clearPreviousStates={clearPreviousStates}
            exitButtonDisable={
              _.get(formValues, "isPublished", "") == 1 ? true : false
            }
            handleConfirmationModal={handleConfirmationModal}
            showConfirmStatuModal={showConfirmStatuModal}
          />
        </div>
      </div>
      <BasicPlanModal
        userDetails={userDetails}
        show={showBasicPlanModal}
        updateReloadParam={updateReloadParam}
        onHide={BasicPlanModalClose}
        fromInventoryListing={true}
        fromInventoryCreation={true}
        showPlanModal={BasicPlanModalShow}
      />
    </div>
  );
};

export default ConsumerCreateInventory;
